import { SideModal } from "shared";
import SVG from "shared/SVG";
import { SVG_ENUM } from "enums";
import { useState } from "react";
import "./RealEstateDetailsSideModal.scss";

interface RealEstateDetailsSideModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
}

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  apartment: "Mieszkanie",
  house: "Dom",
  land: "Działka",
  other: "Inne",
};

const RealEstateDetailsSideModal = ({
  isOpen,
  onClose,
  item,
}: RealEstateDetailsSideModalProps) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const photoFiles = (item?.files ?? []).filter((f: any) => f.role === "photo");

  const contractFiles = (item?.files ?? []).filter(
    (f: any) => f.role === "contract",
  );

  const handleDownloadAll = () => {
    if (!item) return;
    contractFiles.forEach((file: any) => {
      const link = document.createElement("a");
      link.href = file.file.url;
      link.download = file.file.name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) =>
      prev === 0 ? photoFiles.length - 1 : prev - 1,
    );
  };

  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) =>
      prev === photoFiles.length - 1 ? 0 : prev + 1,
    );
  };

  const formatPrice = () => {
    if (!item?.primaryValue) return "Brak";
    const formatted = Number(item.primaryValue).toLocaleString("pl-PL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    const currency = item.currency?.currency ?? "";
    return currency ? `${formatted} ${currency}` : formatted;
  };

  const formatPricePerSqm = () => {
    const area = Number(item?.details?.area);
    const price = Number(item?.primaryValue);
    if (!area || !price || area === 0) return "Brak";
    const perSqm = price / area;
    const currency = item.currency?.currency ?? "";
    const formatted = perSqm.toLocaleString("pl-PL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return currency ? `${formatted} ${currency}` : formatted;
  };

  const getPropertyTypeLabel = () => {
    const propertyType = item?.details?.propertyType;
    if (!propertyType) return "Brak";
    if (propertyType === "other") {
      return item?.details?.propertyTypeOther || "Inne";
    }
    return PROPERTY_TYPE_LABELS[propertyType] ?? propertyType;
  };

  const getAreaDisplay = () => {
    const area = item?.details?.area;
    if (area === null || area === undefined || area === "") return "Brak";
    return `${Number(area).toLocaleString("pl-PL")} m²`;
  };

  return (
    <SideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Szczegółowe informacje"
      subtitle="Aktywo: Nieruchomości"
      className="side-modal--real-estate-details"
    >
      <div className="real-estate-details">
        <div className="real-estate-details__price-header">
          Cena zakupu: <span>{formatPrice()}</span>
        </div>

        <div className="real-estate-details__field">
          <span className="real-estate-details__label">Typ nieruchomości</span>
          <span className="real-estate-details__value">
            {getPropertyTypeLabel()}
          </span>
        </div>

        <div className="real-estate-details__field">
          <span className="real-estate-details__label">Cena zakupu</span>
          <span className="real-estate-details__value">{formatPrice()}</span>
        </div>

        <div className="real-estate-details__field">
          <span className="real-estate-details__label">Waluta</span>
          <span className="real-estate-details__value">
            {item?.currency?.currency || "Brak"}
          </span>
        </div>

        <div className="real-estate-details__field">
          <span className="real-estate-details__label">Miejscowość</span>
          <span className="real-estate-details__value">
            {item?.details?.city || "Brak"}
          </span>
        </div>

        <div className="real-estate-details__field">
          <span className="real-estate-details__label">Adres</span>
          <span className="real-estate-details__value">
            {item?.details?.address || "Brak"}
          </span>
        </div>

        <div className="real-estate-details__field">
          <span className="real-estate-details__label">Powierzchnia</span>
          <span className="real-estate-details__value">{getAreaDisplay()}</span>
        </div>

        <div className="real-estate-details__field">
          <span className="real-estate-details__label">Cena za m²</span>
          <span className="real-estate-details__value">
            {formatPricePerSqm()}
          </span>
        </div>

        <div className="real-estate-details__field">
          <span className="real-estate-details__label">
            Dodatkowe informacje
          </span>
          <span className="real-estate-details__value">
            {item?.additionalInfo || "Brak"}
          </span>
        </div>

        <div className="real-estate-details__field real-estate-details__field--photos">
          <span className="real-estate-details__label">
            Załączone zdjęcia ({photoFiles.length})
          </span>
          {photoFiles.length > 0 ? (
            <div className="real-estate-details__carousel">
              <div className="real-estate-details__carousel-image-wrapper">
                <img
                  src={photoFiles[currentPhotoIndex]?.file?.url}
                  alt={`Zdjęcie ${currentPhotoIndex + 1}`}
                  className="real-estate-details__carousel-image"
                />

                {photoFiles.length > 1 && (
                  <>
                    <button
                      className="real-estate-details__carousel-arrow real-estate-details__carousel-arrow--prev"
                      onClick={handlePrevPhoto}
                    >
                      <svg
                        width="5"
                        height="8"
                        viewBox="0 0 5 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.87695 0.779297L0.778561 3.91127L3.87695 7.04325"
                          stroke="#151515"
                          strokeWidth="1.55757"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    <button
                      className="real-estate-details__carousel-arrow real-estate-details__carousel-arrow--next"
                      onClick={handleNextPhoto}
                    >
                      <svg
                        width="5"
                        height="8"
                        viewBox="0 0 5 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.87695 0.779297L0.778561 3.91127L3.87695 7.04325"
                          stroke="#151515"
                          strokeWidth="1.55757"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {photoFiles.length > 1 && (
                <div className="real-estate-details__carousel-dots">
                  {photoFiles.map((_: any, index: number) => (
                    <button
                      key={index}
                      className={`real-estate-details__carousel-dot ${
                        index === currentPhotoIndex
                          ? "real-estate-details__carousel-dot--active"
                          : ""
                      }`}
                      onClick={() => setCurrentPhotoIndex(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span className="real-estate-details__value">Brak</span>
          )}
        </div>

        <div className="real-estate-details__field real-estate-details__field--files">
          <div className="real-estate-details__files-header">
            <span className="real-estate-details__label">Umowa</span>
            {contractFiles.length > 0 && (
              <button
                className="real-estate-details__download-all"
                onClick={handleDownloadAll}
              >
                Pobierz wszystko
                <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
              </button>
            )}
          </div>
          {contractFiles.length === 0 ? (
            <span className="real-estate-details__value">Brak</span>
          ) : (
            <div className="real-estate-details__files-list">
              {contractFiles.map((file: any) => (
                <div
                  key={file.file.uuid}
                  className="real-estate-details__file-item"
                >
                  <span className="real-estate-details__file-name">
                    {file.file.name}
                  </span>
                  <a
                    href={file.file.url}
                    download={file.file.name}
                    target="_blank"
                    rel="noreferrer"
                    className="real-estate-details__file-download"
                  >
                    <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SideModal>
  );
};

export default RealEstateDetailsSideModal;
